import {Injectable} from "@angular/core";
import {Router} from "@angular/router";
import {forkJoin, Observable} from "rxjs";
import {PitNavigationMenuItem} from "../domain/pit-navigation-menu-item";
import {DxHttpDispatcher} from "../../dx-core/services/dx-http-dispatcher.service";
import {DxObjectMapper} from "../../dx-core/services/dx-object-mapper.service";
import {catchError, map} from "rxjs/operators";
import {HttpResponse} from "@angular/common/http";
import {fromPromise} from "rxjs/internal-compatibility";
import {DxAppRoutes} from 'src/app/dx-core/config/dx-app-routes';

@Injectable()
export class PitNavigationService {

  constructor(private _router: Router,
              private _http: DxHttpDispatcher,
              private _mapper: DxObjectMapper) {

  }

  public redirectToLoginPage(): void {
    this._router.navigate([DxAppRoutes.NAVIGATOR_ROUTE]);
  }


  /**
   * Возвращает верхнее навигационное меню по <пока не ясно на основе чего>.
   * @return {Observable<PitNavigationMenuItem[]>} - плоская иерархия элементов меню.
   */
  public fetchTopMenu(): Observable<PitNavigationMenuItem[]> {
    return this._http.get('top-menu').pipe(
      map((response: HttpResponse<any>) => {
      return this._mapper.deserializeASArray<PitNavigationMenuItem>(response.body, PitNavigationMenuItem);
    }), catchError((errorResponse: HttpResponse<any>) => {
      // Если пункты меню для навигатора не приехали, то продолжать работу
      // системы не имеет смысла
      throw DxHttpDispatcher.handleHttpError(errorResponse);
    }));
  }


  /**
   * Возвращает боковое навигационное меню для элемента верхенего навигационного меню.
   * @param {number} owner - идентификатор элемента верхенего навигационного меню.
   * @return {Observable<PitNavigationMenuItem[]>} - плоская иерархия элементов меню.
   */
  public fetchSideMenu(owner: number): Observable<PitNavigationMenuItem[]> {
    return this._http.get('side-menu').pipe(
      map((response: HttpResponse<any>) => {
        return this._mapper.deserializeASArray<PitNavigationMenuItem>(response.body, PitNavigationMenuItem);
      }), catchError((errorResponse: HttpResponse<any>) => {
        // Если пункты меню для навигатора не приехали, то продолжать работу
        // системы не имеет смысла
        throw DxHttpDispatcher.handleHttpError(errorResponse);
      }));
  }


  /**
   * Возвращает в тестовом режиме мапу боковых навигационных менюх для элементов верхенего навигационного меню.
   * @param {number[]} owners - массив идентификаторов элементов верхенего навигационного меню.
   * @return {Map<number, PitNavigationMenuItem[]>} - мапа владелец - плоская иерархия элементов меню.
   */
  public fetchSideMenus(owners: number[]): Observable<Map<number, PitNavigationMenuItem[]>> {
    const asyncTasks: any[] = [];
    owners.forEach(owner => {
      asyncTasks.push(this.fetchSideMenu(owner));
    });
    let promise: Promise<Map<number, PitNavigationMenuItem[]>> = new Promise((resolve => {
      const result: Map<number, PitNavigationMenuItem[]> = new Map();
      forkJoin(asyncTasks).subscribe(data => {
        if(data) {
          for(let i = 0; i < owners.length; i++) {
            result.set(owners[i], <PitNavigationMenuItem[]> data[i]);
          }
        }
        resolve(result);
      });
    }));
    return fromPromise(promise);
  }



  /** Генерирует моки для верхнего навигационного меню */
  public static provideTopMenuMocks(): Object[] {
    const menuItems: PitNavigationMenuItem[] = [];

    let item = new PitNavigationMenuItem();
    item.id = 1;
    item.ord = 1;
    item.title = "Планы";
    item.ident = "PIT_PLAN";
    item.hasSubMenu = false;
    item.route = 'planning';
    item.hint = "Планы";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 2;
    item.ord = 2;
    item.title = "Мероприятия";
    item.ident = "PIT_ACTIVITIES";
    item.hasSubMenu = false;
    item.route = 'it-activities';
    item.hint = "Мероприятия ИКТ";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 3;
    item.ord = 3;
    item.title = "Согласование";
    item.ident = "PIT_AGREEMENT";
    item.hasSubMenu = false;
    item.route = 'agreement';
    item.hint = "Согласование";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 4;
    item.ord = 4;
    item.title = "Закупочные инициативы";
    item.ident = "PIT_PROCUREMENTS";
    item.hasSubMenu = false;
    item.route = 'procurements';
    item.hint = "Закупочные инициативы";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 5;
    item.ord = 5;
    item.title = "Контракты";
    item.ident = "PIT_CONTRACTS";
    item.hasSubMenu = false;
    item.route = 'contracts';
    item.hint = "Контракты";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 6;
    item.ord = 6;
    item.title = "Отчеты";
    item.ident = "PIT_REPORTS";
    item.hasSubMenu = false;
    item.route = 'reporting';
    item.hint = "Отчеты";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 7;
    item.ord = 7;
    item.title = "НСИ";
    item.ident = "PIT_NCI";
    item.hasSubMenu = false;
    item.route = 'nci';
    item.hint = "Работа со справочниками";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 8;
    item.ord = 8;
    item.title = "Информационная панель";
    item.ident = "PIT_DASHBOARD";
    item.hasSubMenu = false;
    item.route = 'dashboard';
    item.hint = "Информационная панель министра";
    menuItems.push(item);

    return DxObjectMapper.toPlainArray(menuItems, PitNavigationMenuItem);
  }


  /** Генерирует моки для бокового навигационного меню */
  public static provideSideMenuMocks(): Object[] {
    const menuItems: PitNavigationMenuItem[] = [];

    let item = new PitNavigationMenuItem();
    item.id = 1;
    item.ord = 1;
    item.title = "Управление файлами";
    item.ident = "PIT_FILES";
    item.route = 'model-control';
    item.involvedInRouting = false;
    item.icon = "assets/img/dx-img/dx-navigation/icon-1.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 2;
    item.ord = 2;
    item.title = "Управление регламентом";
    item.ident = "PIT_EVENTS";
    item.route = 'reglament-control';
    item.icon = "assets/img/dx-img/dx-navigation/icon-3.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 3;
    item.ord = 3;
    item.title = "Проекты";
    item.ident = "PIT_PROJECTS";
    item.route = 'projects';
    item.involvedInRouting = false;
    item.icon = "assets/img/dx-img/dx-navigation/icon-4.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 4;
    item.ord = 4;
    item.title = "Программы";
    item.ident = "PIT_PROGRAMS";
    item.route = 'programs';
    item.icon = "assets/img/dx-img/dx-navigation/icon-5.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 5;
    item.ord = 5;
    item.title = "Отчетность";
    item.ident = "PIT_REPORTS";
    item.route = 'reports';
    item.icon = "assets/img/dx-img/dx-navigation/icon-6.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 6;
    item.ord = 6;
    item.title = "Аналитика";
    item.ident = "PIT_ANALYTICS";
    item.route = 'analytics';
    item.involvedInRouting = false;
    item.icon = "assets/img/dx-img/dx-navigation/icon-7.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 7;
    item.ord = 7;
    item.title = "Библиотека";
    item.ident = "PIT_LIBRARY";
    item.route = 'library';
    item.icon = "assets/img/dx-img/dx-navigation/icon-8.svg";
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);


    // Меню второго уровня для раздела "Управление файлами"
    item = new PitNavigationMenuItem();
    item.id = 8;
    item.parentId = 1;
    item.ord = 1;
    item.title = "Мероприятия";
    item.ident = "PIT_ACTIVITY_FILES";
    item.route = 'activity-files';
    item.hint = "Просмотр файлов из карточек мероприятий";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 9;
    item.parentId = 1;
    item.ord = 2;
    item.title = "Мордели в разработке";
    item.ident = "PIT_MODEL_UNDER_DEVELOPMENT";
    item.route = 'model-development';
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 10;
    item.parentId = 1;
    item.ord = 3;
    item.title = "Модели на тестировании";
    item.ident = "PIT_MODEL_UNDER_TESTING";
    item.route = 'model-testing';
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 11;
    item.parentId = 1;
    item.ord = 3;
    item.title = "Опубликованные модели";
    item.ident = "PIT_PUBLISHED_MODELS";
    item.route = 'model-publishing';
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    // Меню второго уровня для раздела "Проекты"
    item = new PitNavigationMenuItem();
    item.id = 12;
    item.parentId = 3;
    item.ord = 1;
    item.title = "Проекты на стадии оценки";
    item.ident = "PIT_PROJECTS_UNDER_EVALUATION";
    item.route = 'project-evaluation';
    item.hasSubMenu = false;
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 13;
    item.parentId = 3;
    item.ord = 2;
    item.title = "Поекты с утвержденным бюджетом";
    item.ident = "PIT_PROJECTS_WITH_APPROVED_BUDGET";
    item.route = 'project-budgeting';
    item.hasSubMenu = false;
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);


    item = new PitNavigationMenuItem();
    item.id = 14;
    item.parentId = 3;
    item.ord = 3;
    item.title = "Выполненные проекты";
    item.ident = "PIT_COMPLETED_PROJECTS";
    item.route = 'completed-projects';
    item.hasSubMenu = false;
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    // Меню второго уровня для раздела "Отчеты"
    item = new PitNavigationMenuItem();
    item.id = 15;
    item.parentId = 6;
    item.ord = 1;
    item.title = "Регламентные отчеты";
    item.ident = "PIT_REGLAMENT_REPORTS";
    item.route = 'reglament-reports';
    item.hasSubMenu = false;
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    item = new PitNavigationMenuItem();
    item.id = 16;
    item.parentId = 6;
    item.ord = 2;
    item.title = "OLAP-отчеты";
    item.ident = "PIT_OLAP_REPORTS";
    item.route = 'olap-reports';
    item.hasSubMenu = false;
    item.hint = "Большое; длинное; ооооочень длинное пояснение к пункту меню в боковой панели";
    menuItems.push(item);

    return DxObjectMapper.toPlainArray(menuItems, PitNavigationMenuItem);
  }
}
